import type { ChangeEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, RadioGroup, RadioGroupItem, Textarea } from '@monksflow/monks-ui';
import { Upload } from 'lucide-react';
import {
  AUTH_METHOD_DESCRIPTIONS,
  AUTH_METHOD_LABELS,
  type AuthMethod,
} from '@/entities/media-connector';

interface AddAccountAuthMethodFieldsProps {
  supportedMethods: AuthMethod[];
  hasMultipleMethods: boolean;
  authMethod: AuthMethod;
  onAuthMethodChange: (method: AuthMethod) => void;
  token: string;
  onTokenChange: (value: string) => void;
  keyFileName: string;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}

/**
 * The connection-method picker plus whichever credential field it selects —
 * a pasted token for `system_user_token`, an uploaded JSON key for
 * `service_account`. `oauth` needs no field here; it is handled by the
 * popup flow the parent's `onSubmit` kicks off.
 */
export function AddAccountAuthMethodFields({
  supportedMethods,
  hasMultipleMethods,
  authMethod,
  onAuthMethodChange,
  token,
  onTokenChange,
  keyFileName,
  onFileChange,
  fileInputRef,
  disabled,
}: AddAccountAuthMethodFieldsProps) {
  const { t } = useTranslation('knowledge');

  return (
    <>
      {hasMultipleMethods && (
        <div className="space-y-1.5">
          <Label>{t('mediaConnectors.addAccountDialog.connectionMethod')}</Label>
          <RadioGroup
            value={authMethod}
            onValueChange={(v) => onAuthMethodChange(v as AuthMethod)}
            disabled={disabled}
          >
            {supportedMethods.map((method) => {
              const id = `sheet-auth-method-${method}`;
              return (
                <div key={method} className="flex items-start gap-2">
                  <RadioGroupItem value={method} id={id} />
                  <div className="grid gap-0.5 leading-none">
                    <Label htmlFor={id} className="cursor-pointer">
                      {AUTH_METHOD_LABELS[method]}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {AUTH_METHOD_DESCRIPTIONS[method]}
                    </p>
                  </div>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      )}

      {authMethod === 'system_user_token' && (
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="sheet-system-user-token">
            {t('mediaConnectors.addAccountDialog.systemUserToken')}
          </Label>
          <Textarea
            id="sheet-system-user-token"
            placeholder={t('mediaConnectors.addAccountDialog.pasteTheTokenGenerated')}
            value={token}
            onChange={(e) => onTokenChange(e.target.value)}
            disabled={disabled}
            rows={3}
            className="max-h-32 min-w-0 overflow-hidden"
            style={{ fieldSizing: 'fixed' } as React.CSSProperties}
          />
        </div>
      )}

      {authMethod === 'service_account' && (
        <div className="space-y-1.5">
          <Label>{t('mediaConnectors.addAccountDialog.serviceAccountKeyFile')}</Label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={onFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="flex w-full items-center gap-2 rounded-md border border-dashed border-input bg-background px-3 py-3 text-sm text-muted-foreground hover:border-ring hover:text-foreground"
          >
            <Upload className="size-4" />
            {keyFileName || t('mediaConnectors.addAccountDialog.uploadJsonKeyFile')}
          </button>
        </div>
      )}
    </>
  );
}

AddAccountAuthMethodFields.displayName = 'AddAccountAuthMethodFields';
